import React, { useState } from 'react';
import { CallRequestData } from '../types';
import { X, Mail, ChevronDown } from 'lucide-react';
import PaymentForm from './PaymentAuthorizationForm';
import FileAttachmentInput from './FileAttachmentInput';
import PatientHistory from './PatientHistory';
import { useLanguage } from '../context/LanguageContext';

const symptomKeys = [
  'symptom.irregularCycles', 'symptom.heavyBleeding', 'symptom.painfulPeriods', 'symptom.pcosSymptoms',
  'symptom.fertilityConcerns', 'symptom.hormonalImbalance', 'symptom.fatigue', 'symptom.moodChanges',
  'symptom.weightChanges', 'symptom.acne', 'symptom.hairLoss', 'symptom.sleepIssues', 
  'symptom.anxiety', 'symptom.depression', 'symptom.other'
] as const;

const countryCodes = [
  { code: '+65', country: 'Singapore', flag: '🇸🇬', pattern: /^[689]\d{7}$/, timezone: 'Asia/Singapore' },
  { code: '+95', country: 'Myanmar', flag: '🇲🇲', pattern: /^9\d{8,9}$/, timezone: 'Asia/Yangon' },
  { code: '+81', country: 'Japan', flag: '🇯🇵', pattern: /^[789]0\d{8}$/, timezone: 'Asia/Tokyo' },
  { code: '+66', country: 'Thailand', flag: '🇹🇭', pattern: /^[689]\d{8}$/, timezone: 'Asia/Bangkok' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪', pattern: /^7\d{8}$/, timezone: 'Europe/Stockholm' },
];

interface CallRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (requestId: string, amount: number) => void;
  isLoading?: boolean;
}

const CallRequestModal: React.FC<CallRequestModalProps> = ({ isOpen, onClose, onSuccess, isLoading = false }) => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4 | 5 | 6>(1);
  const [selectedCountryCode, setSelectedCountryCode] = useState(countryCodes[0]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  
  // Use the timezone from selected country code instead of browser timezone
  const [selectedTimezone, setSelectedTimezone] = useState(countryCodes[0].timezone);
  const [showTimezoneConfirm, setShowTimezoneConfirm] = useState(false);
  
  // Gmail validation state
  const [gmail, setGmail] = useState('');
  const [confirmGmail, setConfirmGmail] = useState('');
  const [gmailTouched, setGmailTouched] = useState(false);
  const [confirmGmailTouched, setConfirmGmailTouched] = useState(false);
  const [isCheckingHistory, setIsCheckingHistory] = useState(false);
  const [hasHistory, setHasHistory] = useState<boolean | null>(null);
  
  const [formData, setFormData] = useState<Partial<CallRequestData>>({
    consultationType: undefined, // Will be set in step 1
    symptoms: [], patientPhone: '', patientName: '', patientEmail: '',
    requestedDate: '', requestedTime: '', meetingDuration: 20, age: 0,
    gender: 'female', medicalHistory: '', currentMedications: '',
    needsMedicalCertificate: false, preferredCallTime: 'morning',
    additionalNotes: '', urgencyLevel: 'routine'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isCompletingBooking, setIsCompletingBooking] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const validatePhone = (phone: string, countryCode: typeof countryCodes[0]) => !phone || countryCode.pattern.test(phone);

  // Gmail validation functions
  const normalizeGmail = (email: string): string => {
    return email.toLowerCase().trim();
  };

  const isValidGmail = (email: string): boolean => {
    const normalized = normalizeGmail(email);
    return normalized.endsWith('@gmail.com') || normalized.endsWith('@googlemail.com');
  };

  const suggestGmailCorrection = (email: string): string | null => {
    const normalized = normalizeGmail(email);
    const commonTypos: Record<string, string> = {
      '@gmial.com': '@gmail.com',
      '@gmai.com': '@gmail.com',
      '@gmail.co': '@gmail.com',
      '@gmil.com': '@gmail.com',
      '@gmaill.com': '@gmail.com',
      '@gmail.con': '@gmail.com',
      '@gmail.cm': '@gmail.com',
      '@gmal.com': '@gmail.com',
    };

    for (const [typo, correction] of Object.entries(commonTypos)) {
      if (normalized.endsWith(typo)) {
        return normalized.replace(typo, correction);
      }
    }
    return null;
  };

  const checkPatientHistory = async (normalizedGmail: string) => {
    setIsCheckingHistory(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';
      const response = await fetch(
        apiUrl + '/patients/history?gmail=' + encodeURIComponent(normalizedGmail)
      );
      
      if (response.ok) {
        const data = await response.json();
        setHasHistory(data.hasHistory || false);
        return data.hasHistory || false;
      } else {
        setHasHistory(false);
        return false;
      }
    } catch (error) {
      console.error('Error checking patient history:', error);
      setHasHistory(false);
      return false;
    } finally {
      setIsCheckingHistory(false);
    }
  };

  // Get min and max dates for booking (5-day window in selected timezone)
  const getBookingDateRange = () => {
    // Get current time in selected timezone (based on country code)
    const now = new Date();
    
    // Format date in selected timezone to get YYYY-MM-DD
    const formatter = new Intl.DateTimeFormat('en-CA', { 
      timeZone: selectedTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    const todayStr = formatter.format(now); // Returns YYYY-MM-DD in selected timezone
    
    // Calculate max date (today + 5 days)
    const maxDateObj = new Date(now);
    maxDateObj.setDate(maxDateObj.getDate() + 5);
    const maxDateStr = formatter.format(maxDateObj); // Returns YYYY-MM-DD in selected timezone
    
    console.log('📅 Booking window in', selectedTimezone, ':', todayStr, 'to', maxDateStr);
    
    return {
      minDate: todayStr,
      maxDate: maxDateStr
    };
  };

  if (!isOpen) return null;

  // Specific time options - NEW MODEL (9 AM to 9 PM)
  const TIME_SLOTS = [
    { id: '09:00', time: '09:00', label: '🌅 9:00 AM', display: '9:00 AM', hour: 9 },
    { id: '10:00', time: '10:00', label: '🌅 10:00 AM', display: '10:00 AM', hour: 10 },
    { id: '11:00', time: '11:00', label: '🌅 11:00 AM', display: '11:00 AM', hour: 11 },
    { id: '12:00', time: '12:00', label: '☀️ 12:00 PM', display: '12:00 PM', hour: 12 },
    { id: '13:00', time: '13:00', label: '☀️ 1:00 PM', display: '1:00 PM', hour: 13 },
    { id: '14:00', time: '14:00', label: '☀️ 2:00 PM', display: '2:00 PM', hour: 14 },
    { id: '15:00', time: '15:00', label: '☀️ 3:00 PM', display: '3:00 PM', hour: 15 },
    { id: '16:00', time: '16:00', label: '🌤️ 4:00 PM', display: '4:00 PM', hour: 16 },
    { id: '17:00', time: '17:00', label: '🌤️ 5:00 PM', display: '5:00 PM', hour: 17 },
    { id: '18:00', time: '18:00', label: '🌙 6:00 PM', display: '6:00 PM', hour: 18 },
    { id: '19:00', time: '19:00', label: '🌙 7:00 PM', display: '7:00 PM', hour: 19 },
    { id: '20:00', time: '20:00', label: '🌙 8:00 PM', display: '8:00 PM', hour: 20 },
    { id: '21:00', time: '21:00', label: '🌙 9:00 PM', display: '9:00 PM', hour: 21 },
  ];

  // Get available time slots based on clinic hours (9 AM - 9 PM Yangon time)
  // IMPORTANT: Clinic operates 9 AM - 9 PM Yangon time (Asia/Yangon)
  // We need to show what times in the PATIENT's timezone correspond to clinic hours
  const getAvailableTimeSlots = () => {
    const CLINIC_TIMEZONE = 'Asia/Yangon';
    const CLINIC_OPEN_HOUR = 9;  // 9 AM Yangon time
    const CLINIC_CLOSE_HOUR = 21; // 9 PM Yangon time
    
    const availableSlots: typeof TIME_SLOTS = [];
    
    // Get the selected date or today
    const slotDate = formData.requestedDate || new Date().toISOString().split('T')[0];
    
    // For each hour from 9 AM to 9 PM Yangon time, convert to patient's timezone
    for (let yangonHour = CLINIC_OPEN_HOUR; yangonHour <= CLINIC_CLOSE_HOUR; yangonHour++) {
      try {
        // Create a date-time string in Yangon timezone
        const yangonDateTimeStr = `${slotDate}T${yangonHour.toString().padStart(2, '0')}:00:00`;
        
        // Parse this as if it's in Yangon timezone
        // We need to create a Date object that represents this Yangon time
        const testDate = new Date(yangonDateTimeStr + 'Z'); // Start with UTC
        
        // Get what time this Yangon hour is in the patient's timezone
        // Create a formatter for Yangon time
        const yangonFormatter = new Intl.DateTimeFormat('en-US', {
          timeZone: CLINIC_TIMEZONE,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        
        // Create a formatter for patient's timezone
        const patientFormatter = new Intl.DateTimeFormat('en-US', {
          timeZone: selectedTimezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        
        // We need to find what UTC time corresponds to yangonHour in Yangon
        // Then convert that UTC time to patient's timezone
        
        // Create a date in Yangon timezone
        const dateStr = slotDate.split('-'); // [YYYY, MM, DD]
        const year = parseInt(dateStr[0]);
        const month = parseInt(dateStr[1]) - 1; // JS months are 0-indexed
        const day = parseInt(dateStr[2]);
        
        // Create date object - this will be in local browser time initially
        const localDate = new Date(year, month, day, yangonHour, 0, 0);
        
        // Get the offset between Yangon and UTC
        const yangonOffsetStr = new Intl.DateTimeFormat('en-US', {
          timeZone: CLINIC_TIMEZONE,
          timeZoneName: 'longOffset'
        }).format(localDate);
        
        // Better approach: Use a known UTC time and convert
        // Create UTC date for the selected date at yangonHour in Yangon
        // Yangon is UTC+6:30
        const yangonOffsetMinutes = 6.5 * 60; // 390 minutes ahead of UTC
        const utcHour = yangonHour - 6.5; // Convert Yangon hour to UTC
        
        // Create UTC date
        const utcDate = new Date(Date.UTC(year, month, day, Math.floor(utcHour), (utcHour % 1) * 60, 0));
        
        // Now get what hour this is in patient's timezone
        const patientParts = new Intl.DateTimeFormat('en-US', {
          timeZone: selectedTimezone,
          hour: 'numeric',
          minute: 'numeric',
          hour12: false
        }).formatToParts(utcDate);
        
        const patientHour = parseInt(patientParts.find(p => p.type === 'hour')?.value || '0');
        const patientMinute = parseInt(patientParts.find(p => p.type === 'minute')?.value || '0');
        
        // Format for display
        const displayHour = patientHour % 12 || 12;
        const ampm = patientHour >= 12 ? 'PM' : 'AM';
        const timeStr = `${patientHour.toString().padStart(2, '0')}:${patientMinute.toString().padStart(2, '0')}`;
        const displayStr = `${displayHour}:${patientMinute.toString().padStart(2, '0')} ${ampm}`;
        
        // Determine emoji based on hour
        let emoji = '🌅';
        if (patientHour >= 12 && patientHour < 15) emoji = '☀️';
        else if (patientHour >= 15 && patientHour < 18) emoji = '🌤️';
        else if (patientHour >= 18) emoji = '🌙';
        
        console.log(`${yangonHour}:00 Yangon → ${timeStr} ${selectedTimezone} (${displayStr})`);
        
        // Check if this slot already exists in our TIME_SLOTS
        const existingSlot = TIME_SLOTS.find(s => s.time === timeStr);
        
        if (existingSlot) {
          availableSlots.push(existingSlot);
        } else {
          // Create a new slot
          availableSlots.push({
            id: timeStr,
            time: timeStr,
            label: `${emoji} ${displayStr}`,
            display: displayStr,
            hour: patientHour + (patientMinute / 60)
          });
        }
        
      } catch (err) {
        console.error('Error converting Yangon hour', yangonHour, 'to patient timezone:', err);
      }
    }
    
    // Remove duplicates and sort by hour
    const uniqueSlots = availableSlots.filter((slot, index, self) => 
      index === self.findIndex(s => s.time === slot.time)
    ).sort((a, b) => a.hour - b.hour);
    
    // Now filter out past times if selected date is today
    if (formData.requestedDate) {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-CA', { 
        timeZone: selectedTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const todayInTimezone = formatter.format(now);
      
      if (formData.requestedDate === todayInTimezone) {
        // Get current time in patient timezone
        const timeOptions: Intl.DateTimeFormatOptions = {
          timeZone: selectedTimezone,
          hour: 'numeric',
          minute: 'numeric',
          hour12: false
        };
        
        const parts = new Intl.DateTimeFormat('en-US', timeOptions).formatToParts(now);
        const currentHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
        const currentMinutes = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
        const currentTimeDecimal = currentHour + (currentMinutes / 60);
        
        console.log('🕐 Current time in', selectedTimezone, ':', currentHour + ':' + currentMinutes.toString().padStart(2, '0'));
        
        // Filter out past times (with 1 hour buffer)
        const futureSlots = uniqueSlots.filter(slot => {
          const slotAvailable = currentTimeDecimal < (slot.hour - 1);
          console.log('  Slot ' + slot.label + ': ' + (slotAvailable ? '✅ Available' : '❌ Passed'));
          return slotAvailable;
        });
        
        console.log(`📅 ${futureSlots.length} slots available today (within clinic hours 9 AM - 9 PM Yangon time)`);
        return futureSlots;
      }
    }
    
    console.log(`📅 ${uniqueSlots.length} slots available (clinic hours: 9 AM - 9 PM Yangon time)`);
    return uniqueSlots;
  };

  const handleSymptomToggle = (symptom: string) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms?.includes(symptom) ? prev.symptoms.filter(s => s !== symptom) : [...(prev.symptoms || []), symptom]
    }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (step === 1 && !formData.consultationType) {
      newErrors.consultationType = 'Please select a consultation type';
    }
    
    if (step === 2 && !formData.symptoms?.length) {
      newErrors.symptoms = t('error.selectSymptom');
    }
    
    // Step 3: Gmail validation
    if (step === 3) {
      const normalizedGmail = normalizeGmail(gmail);
      const normalizedConfirm = normalizeGmail(confirmGmail);
      
      if (!gmail.trim()) {
        newErrors.gmail = 'Gmail is required';
      } else if (!isValidGmail(gmail)) {
        const suggestion = suggestGmailCorrection(gmail);
        if (suggestion) {
          newErrors.gmail = 'Did you mean ' + suggestion + '?';
        } else {
          newErrors.gmail = 'Please enter a valid Gmail address (@gmail.com or @googlemail.com)';
        }
      }
      
      if (!confirmGmail.trim()) {
        newErrors.confirmGmail = 'Please confirm your Gmail';
      } else if (normalizedGmail !== normalizedConfirm) {
        newErrors.confirmGmail = 'Gmail addresses do not match';
      }
      
      // If follow-up, check history
      if (formData.consultationType === 'FOLLOW_UP' && normalizedGmail === normalizedConfirm && isValidGmail(gmail)) {
        if (hasHistory === false) {
          newErrors.consultationType = 'No previous consultation found. Please choose First-time consultation.';
        }
      }
    }
    
    if (step === 4) {
      if (!formData.patientName?.trim()) newErrors.patientName = t('error.nameRequired');
      if (!formData.requestedDate?.trim()) newErrors.requestedDate = t('error.dateRequired');
      if (!formData.preferredCallTime) newErrors.preferredCallTime = t('error.timeRequired');
      if (!formData.age || formData.age < 1 || formData.age > 120) newErrors.age = t('error.ageInvalid');
      if (!phoneNumber || !phoneNumber.trim()) {
        newErrors.patientPhone = 'Phone number is required for timezone detection';
      } else if (!validatePhone(phoneNumber, selectedCountryCode)) {
        newErrors.patientPhone = t('error.phoneInvalid');
      }
    }
    
    if (step === 5) {
      if (!acceptedTerms) newErrors.terms = 'You must accept the Terms & Conditions to continue';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = async () => { 
    if (validateStep(currentStep)) {
      // Step 3: Check history for follow-up consultations
      if (currentStep === 3 && formData.consultationType === 'FOLLOW_UP') {
        const normalizedGmail = normalizeGmail(gmail);
        const normalizedConfirm = normalizeGmail(confirmGmail);
        
        if (normalizedGmail === normalizedConfirm && isValidGmail(gmail)) {
          const historyExists = await checkPatientHistory(normalizedGmail);
          if (!historyExists) {
            setErrors({ consultationType: 'No previous consultation found. Please go back and choose First-time consultation.' });
            return;
          }
          // Update formData with normalized Gmail
          setFormData(prev => ({ ...prev, patientEmail: normalizedGmail }));
        }
      }
      
      // Step 3: Set normalized Gmail for first-time consultations
      if (currentStep === 3 && formData.consultationType === 'FIRST_TIME') {
        const normalizedGmail = normalizeGmail(gmail);
        setFormData(prev => ({ ...prev, patientEmail: normalizedGmail }));
      }
      
      // When moving from step 4 to step 5, show timezone confirmation
      if (currentStep === 4 && !showTimezoneConfirm) {
        setShowTimezoneConfirm(true);
        return;
      }
      
      setCurrentStep(prev => Math.min(prev + 1, 6) as 1 | 2 | 3 | 4 | 5 | 6);
    }
  };
  const handlePreviousStep = () => { setCurrentStep(prev => Math.max(prev - 1, 1) as 1 | 2 | 3 | 4 | 5 | 6); };

  const handleContinueToPayment = async () => {
    if (validateStep(4) && validateStep(5)) {
      setCurrentStep(6);
    }
  };

  // Helper function to upload attached files after request creation
  const uploadAttachedFiles = async (requestId: string) => {
    if (attachedFiles.length === 0) return;

    const apiUrl = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';
    let successCount = 0;
    let failCount = 0;

    for (const file of attachedFiles) {
      try {
        console.log('📤 Uploading file:', file.name);
        
        // Step 1: Get presigned URL
        const presignedResponse = await fetch(
          `${apiUrl}/call-requests/${requestId}/attachments/presigned-url`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              filename: file.name,
              mimeType: file.type,
              fileSize: file.size
            })
          }
        );

        if (!presignedResponse.ok) {
          console.error('❌ Failed to get presigned URL for:', file.name);
          failCount++;
          continue;
        }

        const { attachmentId, uploadUrl, s3Key } = await presignedResponse.json();
        console.log('✅ Got presigned URL for:', file.name);

        // Step 2: Upload file to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file
        });

        if (!uploadResponse.ok) {
          console.error('❌ Failed to upload to S3:', file.name);
          failCount++;
          continue;
        }
        console.log('✅ Uploaded to S3:', file.name);

        // Step 3: Confirm upload (may fail due to CORS - that's okay)
        try {
          const confirmResponse = await fetch(
            `${apiUrl}/call-requests/${requestId}/attachments/${attachmentId}/confirm`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                filename: file.name,
                mimeType: file.type,
                fileSize: file.size,
                s3Key: s3Key,
                description: 'Medical document'
              })
            }
          );

          if (confirmResponse.ok) {
            console.log('✅ Confirmed upload:', file.name);
            successCount++;
          } else {
            console.warn('⚠️ File uploaded to S3 but confirmation failed (CORS issue):', file.name);
            // File is in S3, just not confirmed in DB yet - backend can fix this
            successCount++; // Count as success since file is uploaded
          }
        } catch (confirmError) {
          console.warn('⚠️ File uploaded to S3 but confirmation failed (CORS issue):', file.name, confirmError);
          // File is in S3, just not confirmed in DB yet - backend can fix this
          successCount++; // Count as success since file is uploaded
        }
      } catch (error) {
        console.error('❌ Failed to upload file:', file.name, error);
        failCount++;
        // Continue with other files even if one fails
      }
    }

    console.log(`📊 Upload summary: ${successCount} succeeded, ${failCount} failed out of ${attachedFiles.length} files`);
  };

  const handlePaymentSuccess = async (paymentIntentId: string, amount: number, discountCode?: string) => {
    setIsCompletingBooking(true);
    
    try {
      const fullPhone = phoneNumber ? selectedCountryCode.code + phoneNumber : '';
      const selectedTime = formData.preferredCallTime; // This is now the specific time (e.g., "18:00")
      
      if (!selectedTime) {
        throw new Error('Please select a consultation time');
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';
      const response = await fetch(
        apiUrl + '/call-requests',
        {
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            consultationType: formData.consultationType,
            source: 'WEBSITE',
            symptoms: formData.symptoms || [], 
            patientPhone: fullPhone || undefined, 
            patientName: formData.patientName || '',
            patientEmail: formData.patientEmail || '',
            requestedDate: formData.requestedDate || '',
            requestedSpecificTime: selectedTime, // ✅ NEW: Specific time
            // timezone is optional - will be auto-detected from phone number
            language: 'english',
            meetingDuration: 20,
            age: formData.age || 0, 
            gender: formData.gender || 'female', 
            medicalHistory: formData.medicalHistory || '',
            currentMedications: formData.currentMedications || '', 
            needsMedicalCertificate: selectedCountryCode.country === 'Myanmar' ? (formData.needsMedicalCertificate || false) : false,
            urgencyLevel: formData.urgencyLevel || 'routine', 
            additionalNotes: formData.additionalNotes || '',
            paymentIntentId: paymentIntentId,
            discountCode: discountCode,
          }),
        }
      );
      
      if (!response.ok) { 
        const errorData = await response.json().catch(() => ({ error: 'Failed to create request' }));
        throw new Error(errorData.error || 'Failed to create consultation request'); 
      }
      
      const data = await response.json();
      const requestId = data.requestId || data.id;
      
      if (!requestId) {
        throw new Error('No request ID returned from server');
      }
      
      // Upload attached files in background (don't wait)
      if (attachedFiles.length > 0) {
        uploadAttachedFiles(requestId).catch(err => 
          console.error('File upload failed:', err)
        );
      }
      
      // Complete booking
      setIsCompletingBooking(false);
      if (onSuccess) {
        onSuccess(requestId, amount);
      }
    } catch (error: any) {
      setErrors({ submit: error.message || 'Payment authorized but failed to create request. Please contact support with payment ID: ' + paymentIntentId });
      setIsCompletingBooking(false);
    }
  };

  const handleFreeConsultation = async (amount: number, discountCode?: string) => {
    setIsCompletingBooking(true);
    
    try {
      const fullPhone = phoneNumber ? selectedCountryCode.code + phoneNumber : '';
      const selectedTime = formData.preferredCallTime; // This is now the specific time (e.g., "18:00")
      
      if (!selectedTime) {
        throw new Error('Please select a consultation time');
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || 'https://0guzh8ema9.execute-api.us-east-1.amazonaws.com/prod';
      const response = await fetch(
        apiUrl + '/call-requests',
        {
          method: 'POST', 
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            consultationType: formData.consultationType,
            source: 'WEBSITE',
            symptoms: formData.symptoms || [], 
            patientPhone: fullPhone || undefined, 
            patientName: formData.patientName || '',
            patientEmail: formData.patientEmail || '',
            requestedDate: formData.requestedDate || '',
            requestedSpecificTime: selectedTime, // ✅ NEW: Specific time
            // timezone is optional - will be auto-detected from phone number
            language: 'english',
            meetingDuration: 20,
            age: formData.age || 0, 
            gender: formData.gender || 'female', 
            medicalHistory: formData.medicalHistory || '',
            currentMedications: formData.currentMedications || '', 
            needsMedicalCertificate: selectedCountryCode.country === 'Myanmar' ? (formData.needsMedicalCertificate || false) : false,
            urgencyLevel: formData.urgencyLevel || 'routine', 
            additionalNotes: formData.additionalNotes || '',
            discountCode: discountCode,
          }),
        }
      );
      
      if (!response.ok) { 
        const errorData = await response.json().catch(() => ({ error: 'Failed to create request' }));
        throw new Error(errorData.error || 'Failed to create consultation request'); 
      }
      
      const data = await response.json();
      const requestId = data.requestId || data.id;
      
      if (!requestId) {
        throw new Error('No request ID returned from server');
      }
      
      // Upload attached files in background (don't wait)
      if (attachedFiles.length > 0) {
        uploadAttachedFiles(requestId).catch(err => 
          console.error('File upload failed:', err)
        );
      }
      
      // Complete booking
      setIsCompletingBooking(false);
      if (onSuccess) {
        onSuccess(requestId, amount);
      }
    } catch (error: any) {
      setErrors({ submit: error.message || 'Failed to create request. Please try again or contact support.' });
      setIsCompletingBooking(false);
    }
  };

  const updateFormData = (field: keyof CallRequestData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      {/* Loading Overlay - Shows while completing booking */}
      {isCompletingBooking && (
        <div className="absolute inset-0 bg-white/95 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="text-center">
            <div className="w-20 h-20 border-4 border-[#38BDF8] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <h3 className="text-2xl font-bold text-[#334155] mb-2">Processing Your Booking...</h3>
            <p className="text-[#334155]/60">Please wait while we confirm your consultation</p>
            <div className="mt-6 flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-[#38BDF8] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-[#38BDF8] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-[#38BDF8] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#334155]/10 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-[#334155]">{t('modal.title')}</h2>
            <p className="text-sm text-[#334155]/50">{t('modal.step')} {currentStep} {t('modal.of')} 6</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F0F9FF] rounded-full transition-colors" disabled={isLoading}>
            <X className="w-5 h-5 text-[#334155]/50" />
          </button>
        </div>

        <div className="px-6 py-3 bg-gradient-to-r from-[#F0F9FF] to-[#FFF1F2]">
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5, 6].map((step) => (
              <div key={step} className={step <= currentStep ? 'h-1.5 flex-1 rounded-full transition-colors bg-[#38BDF8]' : 'h-1.5 flex-1 rounded-full transition-colors bg-[#334155]/10'} />
            ))}
          </div>
        </div>

        <div className="p-6">
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-[#334155] mb-2">Select Consultation Type</h3>
                <p className="text-sm text-[#334155]/50">Choose the type of consultation you need</p>
              </div>
              
              <div className="space-y-3">
                {/* First-time Consultation */}
                <button
                  type="button"
                  onClick={() => updateFormData('consultationType', 'FIRST_TIME')}
                  className={formData.consultationType === 'FIRST_TIME'
                    ? 'w-full p-5 rounded-xl border-2 text-left transition-all border-[#38BDF8] bg-[#F0F9FF] shadow-md'
                    : 'w-full p-5 rounded-xl border-2 text-left transition-all border-[#334155]/20 hover:border-[#38BDF8]/50 bg-white'}
                >
                  <div className="flex items-start gap-4">
                    <div className={formData.consultationType === 'FIRST_TIME'
                      ? 'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 border-[#38BDF8] bg-[#38BDF8]'
                      : 'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 border-[#334155]/30'}>
                      {formData.consultationType === 'FIRST_TIME' && (
                        <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-[#334155] mb-1">First-time Consultation</div>
                      <div className="text-sm text-[#334155]/70 mb-2">
                        This is your first consultation with SheCare
                      </div>
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#38BDF8]/10 rounded-lg">
                        <span className="text-lg font-bold text-[#38BDF8]">$19.99</span>
                        <span className="text-xs text-[#334155]/60">per consultation</span>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Follow-up Consultation */}
                <button
                  type="button"
                  onClick={() => updateFormData('consultationType', 'FOLLOW_UP')}
                  className={formData.consultationType === 'FOLLOW_UP'
                    ? 'w-full p-5 rounded-xl border-2 text-left transition-all border-[#38BDF8] bg-[#F0F9FF] shadow-md'
                    : 'w-full p-5 rounded-xl border-2 text-left transition-all border-[#334155]/20 hover:border-[#38BDF8]/50 bg-white'}
                >
                  <div className="flex items-start gap-4">
                    <div className={formData.consultationType === 'FOLLOW_UP'
                      ? 'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 border-[#38BDF8] bg-[#38BDF8]'
                      : 'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 border-[#334155]/30'}>
                      {formData.consultationType === 'FOLLOW_UP' && (
                        <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-[#334155] mb-1">Follow-up Consultation</div>
                      <div className="text-sm text-[#334155]/70 mb-2">
                        You've had a previous consultation with SheCare
                      </div>
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg">
                        <span className="text-lg font-bold text-green-600">$10.00</span>
                        <span className="text-xs text-[#334155]/60">per consultation</span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {errors.consultationType && (
                <p className="text-[#FDA4AF] text-sm">{errors.consultationType}</p>
              )}

              <button
                onClick={handleNextStep}
                disabled={!formData.consultationType}
                className={formData.consultationType
                  ? 'w-full py-3.5 rounded-xl font-medium transition-all bg-[#38BDF8] text-white hover:bg-[#0EA5E9] shadow-lg shadow-[#38BDF8]/30'
                  : 'w-full py-3.5 rounded-xl font-medium transition-all bg-[#334155]/10 text-[#334155]/40 cursor-not-allowed'}
              >
                Continue
              </button>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-[#334155] mb-2">{t('step1.title')}</h3>
                <p className="text-sm text-[#334155]/50">{t('step1.subtitle')}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {symptomKeys.map((key) => {
                  const symptomText = t(key);
                  return (
                    <button 
                      key={key} 
                      onClick={() => handleSymptomToggle(symptomText)}
                      className={formData.symptoms?.includes(symptomText)
                        ? 'px-4 py-3 rounded-xl text-sm font-medium transition-all bg-[#38BDF8] text-white shadow-md'
                        : 'px-4 py-3 rounded-xl text-sm font-medium transition-all bg-[#F0F9FF] text-[#334155] hover:bg-[#38BDF8]/20 border border-[#38BDF8]/20'}
                    >
                      {symptomText}
                    </button>
                  );
                })}
              </div>
              {errors.symptoms && <p className="text-[#FDA4AF] text-sm">{errors.symptoms}</p>}
              <div className="flex gap-3 pt-2">
                <button onClick={handlePreviousStep} className="flex-1 py-3.5 border border-[#334155]/20 text-[#334155] rounded-xl font-medium hover:bg-[#F0F9FF] transition-all">{t('form.back')}</button>
                <button 
                  onClick={handleNextStep} 
                  disabled={!formData.symptoms?.length}
                  className={formData.symptoms?.length ? 'flex-1 py-3.5 rounded-xl font-medium transition-all bg-[#38BDF8] text-white hover:bg-[#0EA5E9] shadow-lg shadow-[#38BDF8]/30' : 'flex-1 py-3.5 rounded-xl font-medium transition-all bg-[#334155]/10 text-[#334155]/40 cursor-not-allowed'}
                >
                  {t('step1.continue')}
                </button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-medium text-[#334155] mb-2">Enter Your Gmail</h3>
                <p className="text-sm text-[#334155]/50">We'll use this to send your consultation details and check your history</p>
              </div>

              {/* Gmail Field */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">
                  Gmail Address <span className="text-[#FDA4AF]">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#334155]/40" />
                  <input
                    type="email"
                    value={gmail}
                    onChange={(e) => {
                      setGmail(e.target.value);
                      setGmailTouched(true);
                      setHasHistory(null); // Reset history check when Gmail changes
                      if (errors.gmail) setErrors(prev => ({ ...prev, gmail: '' }));
                      if (errors.consultationType) setErrors(prev => ({ ...prev, consultationType: '' }));
                    }}
                    onBlur={() => setGmailTouched(true)}
                    className="w-full pl-11 pr-4 py-3 border border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-transparent transition-all bg-white"
                    placeholder="yourname@gmail.com"
                  />
                </div>
                {errors.gmail && <p className="text-[#FDA4AF] text-xs mt-1">{errors.gmail}</p>}
                <p className="text-xs text-[#334155]/40 mt-1">Must be a Gmail address (@gmail.com or @googlemail.com)</p>
              </div>

              {/* Confirm Gmail Field */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">
                  Confirm Gmail Address <span className="text-[#FDA4AF]">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#334155]/40" />
                  <input
                    type="email"
                    value={confirmGmail}
                    onChange={(e) => {
                      setConfirmGmail(e.target.value);
                      setConfirmGmailTouched(true);
                      setHasHistory(null); // Reset history check when confirm Gmail changes
                      if (errors.confirmGmail) setErrors(prev => ({ ...prev, confirmGmail: '' }));
                      if (errors.consultationType) setErrors(prev => ({ ...prev, consultationType: '' }));
                    }}
                    onBlur={() => setConfirmGmailTouched(true)}
                    className="w-full pl-11 pr-4 py-3 border border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-transparent transition-all bg-white"
                    placeholder="yourname@gmail.com"
                  />
                </div>
                {errors.confirmGmail && <p className="text-[#FDA4AF] text-xs mt-1">{errors.confirmGmail}</p>}
                {gmailTouched && confirmGmailTouched && gmail && confirmGmail && normalizeGmail(gmail) === normalizeGmail(confirmGmail) && (
                  <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Gmail addresses match
                  </p>
                )}
              </div>

              {/* History Check Status for Follow-up */}
              {formData.consultationType === 'FOLLOW_UP' && (
                <div className="p-4 bg-[#F0F9FF] border border-[#38BDF8]/20 rounded-xl">
                  {isCheckingHistory ? (
                    <div className="flex items-center gap-2 text-[#334155]">
                      <div className="w-4 h-4 border-2 border-[#38BDF8] border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm">Checking your consultation history...</span>
                    </div>
                  ) : hasHistory === true ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 text-green-600">
                        <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <div className="font-medium text-sm">Follow-up consultation confirmed</div>
                          <div className="text-xs text-[#334155]/60">Previous consultation found. Follow-up price ($10) will be applied.</div>
                        </div>
                      </div>
                      
                      {/* Patient History Component */}
                      {gmail && confirmGmail && normalizeGmail(gmail) === normalizeGmail(confirmGmail) && (
                        <PatientHistory email={normalizeGmail(gmail)} />
                      )}
                    </div>
                  ) : hasHistory === false ? (
                    <div>
                      <div className="flex items-start gap-2 text-[#FDA4AF] mb-3">
                        <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1">
                          <div className="font-medium text-sm">No previous consultation found</div>
                          <div className="text-xs text-[#334155]/60">Please go back and select "First-time consultation" or check your Gmail address</div>
                        </div>
                      </div>
                      {gmail && confirmGmail && normalizeGmail(gmail) === normalizeGmail(confirmGmail) && isValidGmail(gmail) && (
                        <button
                          type="button"
                          onClick={async () => {
                            const normalizedGmail = normalizeGmail(gmail);
                            await checkPatientHistory(normalizedGmail);
                          }}
                          className="w-full py-2 px-4 bg-[#38BDF8] text-white rounded-lg text-sm font-medium hover:bg-[#0EA5E9] transition-colors"
                        >
                          Check Again
                        </button>
                      )}
                    </div>
                  ) : gmail && confirmGmail && normalizeGmail(gmail) === normalizeGmail(confirmGmail) && isValidGmail(gmail) ? (
                    <button
                      type="button"
                      onClick={async () => {
                        const normalizedGmail = normalizeGmail(gmail);
                        await checkPatientHistory(normalizedGmail);
                      }}
                      className="w-full py-3 px-4 bg-[#38BDF8] text-white rounded-lg text-sm font-medium hover:bg-[#0EA5E9] transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Check Consultation History
                    </button>
                  ) : (
                    <div className="text-sm text-[#334155]/60 text-center py-2">
                      Enter matching Gmail addresses to check your consultation history
                    </div>
                  )}
                </div>
              )}

              {/* Pricing Display */}
              <div className="p-4 bg-gradient-to-r from-[#F0F9FF] to-[#FFF1F2] rounded-xl border border-[#38BDF8]/20">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-[#334155]/60">Consultation Fee</div>
                    <div className="text-xs text-[#334155]/50 mt-0.5">
                      {formData.consultationType === 'FIRST_TIME' ? 'First-time consultation' : 'Follow-up consultation'}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-[#38BDF8]">
                    {formData.consultationType === 'FIRST_TIME' ? '$19.99' : '$10.00'}
                  </div>
                </div>
              </div>

              {errors.consultationType && (
                <div className="p-4 bg-[#FDA4AF]/10 border border-[#FDA4AF] rounded-xl">
                  <p className="text-[#FDA4AF] text-sm">{errors.consultationType}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button onClick={handlePreviousStep} className="flex-1 py-3.5 border border-[#334155]/20 text-[#334155] rounded-xl font-medium hover:bg-[#F0F9FF] transition-all">
                  {t('form.back')}
                </button>
                <button
                  onClick={handleNextStep}
                  disabled={!gmail || !confirmGmail || normalizeGmail(gmail) !== normalizeGmail(confirmGmail) || !isValidGmail(gmail) || (formData.consultationType === 'FOLLOW_UP' && hasHistory === false)}
                  className={
                    gmail && confirmGmail && normalizeGmail(gmail) === normalizeGmail(confirmGmail) && isValidGmail(gmail) && (formData.consultationType === 'FIRST_TIME' || hasHistory === true)
                      ? 'flex-1 py-3.5 rounded-xl font-medium transition-all bg-[#38BDF8] text-white hover:bg-[#0EA5E9] shadow-lg shadow-[#38BDF8]/30'
                      : 'flex-1 py-3.5 rounded-xl font-medium transition-all bg-[#334155]/10 text-[#334155]/40 cursor-not-allowed'
                  }
                >
                  {t('step1.continue')}
                </button>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-medium text-[#334155] mb-1">{t('step2.title')}</h3>
                <p className="text-sm text-[#334155]/50">{t('step2.subtitle')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">{t('form.fullName')} <span className="text-[#FDA4AF]">*</span></label>
                <input type="text" value={formData.patientName || ''} onChange={(e) => updateFormData('patientName', e.target.value)}
                  className="w-full px-4 py-3 border border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-transparent transition-all bg-white"
                  placeholder={t('form.fullNamePlaceholder')} />
                {errors.patientName && <p className="text-[#FDA4AF] text-xs mt-1">{errors.patientName}</p>}
              </div>

              {/* Gmail is now collected in Step 3 - show read-only display */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">Gmail Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#334155]/40" />
                  <input
                    type="email"
                    value={formData.patientEmail || ''}
                    disabled
                    className="w-full pl-11 pr-4 py-3 border border-[#334155]/20 rounded-xl bg-[#F0F9FF] text-[#334155]/70 cursor-not-allowed"
                  />
                </div>
                <p className="text-xs text-[#334155]/40 mt-1">Gmail confirmed in previous step</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">
                  {t('form.phone')} <span className="text-[#FDA4AF]">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="relative">
                    <select value={selectedCountryCode.code} onChange={(e) => { 
                      const c = countryCodes.find(x => x.code === e.target.value); 
                      if (c) {
                        setSelectedCountryCode(c);
                        setSelectedTimezone(c.timezone);
                        // Reset date and time when timezone changes
                        setFormData(prev => ({
                          ...prev,
                          requestedDate: '',
                          preferredCallTime: ''
                        }));
                      }
                    }}
                      className="appearance-none w-28 px-3 py-3 border border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-transparent bg-[#F0F9FF] text-sm">
                      {countryCodes.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#334155]/40 pointer-events-none" />
                  </div>
                  <input type="tel" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                    className="flex-1 px-4 py-3 border border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-transparent transition-all bg-white"
                    placeholder={t('form.phonePlaceholder')} 
                    required />
                </div>
                {errors.patientPhone && <p className="text-[#FDA4AF] text-xs mt-1">{errors.patientPhone}</p>}
                <div className="mt-2 p-2 bg-[#F0F9FF] rounded-lg border border-[#38BDF8]/20">
                  <p className="text-xs text-[#334155]/70">
                    🌍 Timezone: <span className="font-medium">{selectedCountryCode.country}</span> ({selectedTimezone})
                  </p>
                  <p className="text-xs text-[#334155]/70 mt-0.5">
                    🕐 Current time: <span className="font-medium">
                      {new Intl.DateTimeFormat('en-US', {
                        timeZone: selectedTimezone,
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      }).format(new Date())}
                    </span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#334155] mb-1.5">{t('form.age')} <span className="text-[#FDA4AF]">*</span></label>
                  <input type="number" value={formData.age || ''} onChange={(e) => updateFormData('age', parseInt(e.target.value) || 0)}
                    className="w-full px-4 py-3 border border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-transparent bg-white"
                    placeholder={t('form.agePlaceholder')} min="1" max="120" />
                  {errors.age && <p className="text-[#FDA4AF] text-xs mt-1">{errors.age}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#334155] mb-1.5">{t('form.gender')}</label>
                  <select value={formData.gender || 'female'} onChange={(e) => updateFormData('gender', e.target.value)}
                    className="w-full px-4 py-3 border border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-transparent bg-white">
                    <option value="female">{t('form.female')}</option>
                    <option value="male">{t('form.male')}</option>
                    <option value="other">{t('form.other')}</option>
                    <option value="prefer-not-to-say">{t('form.preferNotToSay')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">{t('form.preferredDate')} <span className="text-[#FDA4AF]">*</span></label>
                <input type="date" value={formData.requestedDate || ''} onChange={(e) => updateFormData('requestedDate', e.target.value)}
                  className="w-full px-4 py-3 border border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-transparent bg-white"
                  min={getBookingDateRange().minDate}
                  max={getBookingDateRange().maxDate} />
                {errors.requestedDate && <p className="text-[#FDA4AF] text-xs mt-1">{errors.requestedDate}</p>}
                <p className="text-xs text-[#334155]/60 mt-1">
                  📅 You can book up to 5 days in advance ({selectedCountryCode.country} time)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">{t('form.preferredTime')} <span className="text-[#FDA4AF]">*</span></label>
                {!formData.requestedDate ? (
                  <div className="p-4 bg-[#F0F9FF] border border-[#38BDF8]/20 rounded-xl text-center">
                    <p className="text-[#334155]/70 text-sm">
                      Please select a date first
                    </p>
                  </div>
                ) : getAvailableTimeSlots().length === 0 ? (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center">
                    <p className="text-yellow-800 text-sm">
                      ⏰ All time slots for today have passed. Please select a future date.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {getAvailableTimeSlots().map((slot) => (
                      <button 
                        key={slot.id} 
                        type="button" 
                        onClick={() => updateFormData('preferredCallTime', slot.time)}
                        className={formData.preferredCallTime === slot.time 
                          ? 'p-3 rounded-xl border-2 text-center transition-all border-[#38BDF8] bg-[#F0F9FF]' 
                          : 'p-3 rounded-xl border-2 text-center transition-all border-[#334155]/20 hover:border-[#38BDF8]/50'}
                      >
                        <div className="text-sm font-medium text-[#334155]">{slot.label}</div>
                        <div className="text-xs text-[#334155]/60 mt-0.5">{slot.display}</div>
                      </button>
                    ))}
                  </div>
                )}
                {errors.preferredCallTime && <p className="text-[#FDA4AF] text-xs mt-1">{errors.preferredCallTime}</p>}
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={handlePreviousStep} className="flex-1 py-3.5 border border-[#334155]/20 text-[#334155] rounded-xl font-medium hover:bg-[#F0F9FF] transition-all">{t('form.back')}</button>
                <button onClick={handleNextStep} className="flex-1 py-3.5 bg-[#38BDF8] text-white rounded-xl font-medium hover:bg-[#0EA5E9] shadow-lg shadow-[#38BDF8]/30 transition-all">{t('step1.continue')}</button>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-medium text-[#334155] mb-1">{t('step3.title')}</h3>
                <p className="text-sm text-[#334155]/50">{t('step3.subtitle')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">{t('form.medicalHistory')} <span className="text-[#334155]/40">{t('form.optional')}</span></label>
                <textarea value={formData.medicalHistory || ''} onChange={(e) => updateFormData('medicalHistory', e.target.value)}
                  className="w-full px-4 py-3 border border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-transparent resize-none bg-white"
                  placeholder={t('form.medicalHistoryPlaceholder')} rows={3} />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">{t('form.currentMedications')} <span className="text-[#334155]/40">{t('form.optional')}</span></label>
                <textarea value={formData.currentMedications || ''} onChange={(e) => updateFormData('currentMedications', e.target.value)}
                  className="w-full px-4 py-3 border border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-transparent resize-none bg-white"
                  placeholder={t('form.currentMedicationsPlaceholder')} rows={2} />
              </div>

              {/* Medical Certificate - Only show for Myanmar patients */}
              {selectedCountryCode.country === 'Myanmar' && (
                <label className="flex items-center gap-3 p-4 bg-[#F0F9FF] rounded-xl cursor-pointer hover:bg-[#38BDF8]/10 transition-colors">
                  <input type="checkbox" checked={formData.needsMedicalCertificate || false}
                    onChange={(e) => updateFormData('needsMedicalCertificate', e.target.checked)}
                    className="w-5 h-5 text-[#38BDF8] border-[#334155]/30 rounded focus:ring-[#38BDF8]" />
                  <div>
                    <div className="font-medium text-[#334155]">{t('form.medicalCertificate')}</div>
                    <div className="text-sm text-[#334155]/50">{t('form.medicalCertificateNote')}</div>
                  </div>
                </label>
              )}

              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">{t('form.additionalNotes')} <span className="text-[#334155]/40">{t('form.optional')}</span></label>
                <textarea value={formData.additionalNotes || ''} onChange={(e) => updateFormData('additionalNotes', e.target.value)}
                  className="w-full px-4 py-3 border border-[#334155]/20 rounded-xl focus:ring-2 focus:ring-[#38BDF8] focus:border-transparent resize-none bg-white"
                  placeholder={t('form.additionalNotesPlaceholder')} rows={2} />
              </div>

              {/* File Attachments */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">
                  Medical Documents <span className="text-[#334155]/40">(Optional)</span>
                </label>
                <FileAttachmentInput 
                  files={attachedFiles}
                  onFilesChange={setAttachedFiles}
                  maxFiles={5}
                />
                <p className="text-xs text-[#334155]/60 mt-2">
                  Attach lab results, prescriptions, or images to help your doctor understand your condition better
                </p>
              </div>

              {/* Terms & Conditions Acceptance */}
              <div className="border-t border-[#334155]/10 pt-5">
                <label className="flex items-start gap-3 p-4 bg-[#FFF1F2] border border-[#FDA4AF]/20 rounded-xl cursor-pointer hover:bg-[#FDA4AF]/5 transition-colors">
                  <input 
                    type="checkbox" 
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      if (e.target.checked && errors.terms) {
                        setErrors(prev => ({ ...prev, terms: '' }));
                      }
                    }}
                    className="w-5 h-5 text-[#38BDF8] border-[#334155]/30 rounded focus:ring-[#38BDF8] mt-0.5 flex-shrink-0" 
                  />
                  <div className="text-sm">
                    <span className="text-[#334155]">
                      I accept the{' '}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowTermsModal(true);
                        }}
                        className="text-[#38BDF8] hover:text-[#0EA5E9] underline font-medium"
                      >
                        Terms & Conditions
                      </button>
                      {' '}and understand that SheCare provides guidance-only consultations, not emergency services or formal diagnoses.
                    </span>
                  </div>
                </label>
                {errors.terms && (
                  <p className="text-[#FDA4AF] text-xs mt-2 ml-1">{errors.terms}</p>
                )}
              </div>

              {errors.submit && (
                <div className="p-4 bg-[#FDA4AF]/20 border border-[#FDA4AF] rounded-xl">
                  <p className="text-[#334155] text-sm">{errors.submit}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button onClick={handlePreviousStep}
                  className="flex-1 py-3.5 border border-[#334155]/20 text-[#334155] rounded-xl font-medium hover:bg-[#F0F9FF] transition-all">{t('form.back')}</button>
                <button 
                  onClick={handleContinueToPayment} 
                  disabled={!acceptedTerms}
                  className={!acceptedTerms ? 'flex-1 py-3.5 rounded-xl font-medium transition-all bg-[#334155]/20 text-[#334155]/50 cursor-not-allowed' : 'flex-1 py-3.5 rounded-xl font-medium transition-all bg-[#38BDF8] text-white hover:bg-[#0EA5E9] shadow-lg shadow-[#38BDF8]/30'}
                >
                  {t('form.continueToPayment')}
                </button>
              </div>
            </div>
          )}

          {currentStep === 6 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-[#334155] mb-1">{t('step4.title')}</h3>
                <p className="text-sm text-[#334155]/50">{t('step4.subtitle')}</p>
              </div>
              
              {errors.submit && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-700 text-sm mb-3">{errors.submit}</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setErrors({});
                        onClose();
                      }}
                      className="flex-1 py-2.5 bg-[#334155] text-white rounded-lg font-medium hover:bg-[#1e293b] transition-all"
                    >
                      Go Back Home
                    </button>
                    <button
                      onClick={() => setErrors({})}
                      className="flex-1 py-2.5 border border-[#334155]/20 text-[#334155] rounded-lg font-medium hover:bg-[#F0F9FF] transition-all"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}
              
              {!errors.submit && (
                <PaymentForm 
                  requestId=""
                  onSuccess={handlePaymentSuccess} 
                  onFreeConsultation={handleFreeConsultation}
                  onCancel={handlePreviousStep} 
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-[#334155]/10">
              <h2 className="text-2xl font-bold text-[#334155]">Terms & Conditions</h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="p-2 hover:bg-[#F0F9FF] rounded-lg transition-colors"
              >
                <svg className="w-6 h-6 text-[#334155]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="overflow-y-auto p-6 space-y-6">
              {/* Introduction */}
              <section>
                <h3 className="text-xl font-semibold text-[#334155] mb-3">1. Introduction</h3>
                <div className="text-[#334155]/70 leading-relaxed space-y-2">
                  <p>Welcome to SheCare. By accessing or using our platform, you agree to be bound by these Terms and Conditions.</p>
                  <p>SheCare provides online health consultations for educational and guidance purposes. Please read these terms carefully before using our services.</p>
                </div>
              </section>

              {/* Scope of Services */}
              <section>
                <h3 className="text-xl font-semibold text-[#334155] mb-3">2. Scope of Services</h3>
                <div className="text-[#334155]/70 leading-relaxed space-y-2">
                  <p>SheCare provides online consultations for educational and guidance purposes. SheCare does not replace in-person medical care, emergency services, or long-term treatment.</p>
                  <p>Our consultations are designed to help you understand your health concerns, provide reassurance, and guide you on appropriate next steps — including when to seek local medical care.</p>
                </div>
              </section>

              {/* No Emergency Service */}
              <section>
                <h3 className="text-xl font-semibold text-[#334155] mb-3">3. No Emergency Service</h3>
                <div className="bg-[#FFF1F2] border border-[#FDA4AF]/30 rounded-xl p-4">
                  <p className="text-[#334155] leading-relaxed"><strong>Important:</strong> SheCare is not intended for medical emergencies. Users experiencing emergencies must seek immediate local medical care.</p>
                  <p className="text-[#334155]/70 leading-relaxed mt-2">If you are experiencing severe pain, heavy bleeding, chest pain, difficulty breathing, or any other medical emergency, please call your local emergency services or go to the nearest hospital immediately.</p>
                </div>
              </section>

              {/* Medical Disclaimer */}
              <section>
                <h3 className="text-xl font-semibold text-[#334155] mb-3">4. Medical Disclaimer</h3>
                <div className="text-[#334155]/70 leading-relaxed space-y-2">
                  <p>Information provided during consultations is not a substitute for physical examination, diagnosis, or treatment by a local healthcare provider.</p>
                  <p>SheCare consultations are intended to provide guidance and education. They do not constitute a doctor-patient relationship for the purposes of diagnosis, prescription, or ongoing treatment.</p>
                </div>
              </section>

              {/* User Responsibilities */}
              <section>
                <h3 className="text-xl font-semibold text-[#334155] mb-3">5. User Responsibilities</h3>
                <div className="text-[#334155]/70 leading-relaxed space-y-2">
                  <p>As a user of SheCare, you agree to:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Provide accurate and complete information during consultations</li>
                    <li>Use the service for its intended purpose of guidance and education</li>
                    <li>Seek appropriate local medical care when advised or needed</li>
                    <li>Not use the service for emergencies or urgent medical situations</li>
                  </ul>
                </div>
              </section>

              {/* Payments & Refunds */}
              <section>
                <h3 className="text-xl font-semibold text-[#334155] mb-3">6. Payments & Refunds</h3>
                <div className="text-[#334155]/70 leading-relaxed space-y-2">
                  <p>Consultation fees are clearly displayed before booking. Payment is required at the time of booking.</p>
                  <p>Refunds may be provided in cases where the consultation could not be completed due to technical issues on our end or the doctor was unable to attend.</p>
                </div>
              </section>

              {/* Privacy */}
              <section>
                <h3 className="text-xl font-semibold text-[#334155] mb-3">7. Privacy & Data Protection</h3>
                <div className="text-[#334155]/70 leading-relaxed">
                  <p>We take your privacy seriously. All personal and medical information is handled securely and confidentially in accordance with our Privacy Policy.</p>
                </div>
              </section>

              {/* Acceptance */}
              <section className="bg-[#F0F9FF] border border-[#38BDF8]/20 rounded-xl p-4">
                <p className="text-[#334155] text-center">
                  By using SheCare, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
                </p>
              </section>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-[#334155]/10">
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-full py-3 bg-[#38BDF8] text-white rounded-xl font-medium hover:bg-[#0EA5E9] transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallRequestModal;
